import { LOCALE_STORAGE_KEY } from './i18n.ts';
import { deleteDb } from './drafts/store.ts';
import { deleteUploadJournal } from './upload/journal.ts';

/**
 * What sign-out leaves behind on this browser (roadmap 3.3, 3.4; B-10): nothing readable.
 *
 * 1. The browser's push subscription is cancelled while the service worker still exists.
 * 2. The server revokes the session and destroys its draft key (POST /auth/logout, the caller's step).
 * 3. Here: the IndexedDB databases `mdt` (drafts, stored keys) and `mdt-uploads` (the upload resume journal),
 *    every Cache Storage entry, session storage, and local storage except the language choice.
 *
 * The logout response also carries Clear-Site-Data: "cache", "storage", which does all of this again in
 * browsers that support it (it also unregisters the service worker). If this wipe never runs (the tab was
 * killed), what remains is ciphertext under a destroyed key, purged on the next load.
 */
export async function unsubscribeThisBrowser(): Promise<string | null> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    return endpoint;
  } catch {
    return null;
  }
}

export async function wipeLocalData(): Promise<void> {
  await Promise.allSettled([
    deleteDb(),
    deleteUploadJournal(),
    (async () => {
      if (!('caches' in globalThis)) return;
      for (const name of await caches.keys()) await caches.delete(name);
    })(),
  ]);
  try {
    sessionStorage.clear();
  } catch {
    // storage disabled
  }
  try {
    const locale = localStorage.getItem(LOCALE_STORAGE_KEY);
    localStorage.clear();
    if (locale) localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // storage disabled
  }
}
